import { useMemo, useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { ProfileFilter } from '../components/ProfileFilter';
import { MetricStat } from '../components/MetricStat';
import { DashPanel } from '../components/DashPanel';
import { DataCardHeader } from '../components/CardExportButton';
import { exportCardCsv } from '../utils/csvExport';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChartHorizontal } from '../components/charts/DashCharts';
import { computeRealGamesAnalytics, formatResponseTime } from '../utils/realGamesAnalytics';
import type { RoundStats } from '../types/games';

function RoundsTable({
  title,
  rounds,
  onExport,
}: {
  title: string;
  rounds: RoundStats[];
  onExport?: () => void;
}) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wide bg-muted">
            <th className="py-1.5 px-2 w-10 rounded-l-md">#</th>
            <th className="py-1.5 px-2">Comando da rodada</th>
            <th className="py-1.5 px-2 text-right">Acertos</th>
            <th className="py-1.5 px-2 text-right">Erros</th>
            <th className="py-1.5 px-2 text-right rounded-r-md">Taxa de acerto</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((round) => (
            <tr key={round.roundId} className="border-t border-border">
              <td className="py-2 px-2 text-muted-foreground tabular-nums">{round.order}</td>
              <td className="py-2 px-2 text-foreground">{round.prompt}</td>
              <td className="py-2 px-2 text-right text-foreground tabular-nums">{round.correct}</td>
              <td className="py-2 px-2 text-right text-foreground tabular-nums">{round.incorrect}</td>
              <td className="py-2 px-2 text-right font-medium text-foreground tabular-nums">
                {round.accuracyPct}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </DashPanel>
  );
}

export function RoundHighlightCard({
  label,
  round,
}: {
  label: string;
  round: RoundStats | null;
}) {
  return (
    <DashPanel padding="sm">
      <p className="text-xs text-muted-foreground">{label}</p>
      {round ? (
        <>
          <p className="text-sm font-semibold text-foreground mt-1">
            Rodada {round.order} — {round.prompt}
          </p>
          <div className="flex gap-4 mt-2">
            <span className="text-xs text-muted-foreground">
              Erros: <span className="font-medium text-foreground">{round.incorrect}</span>
            </span>
            <span className="text-xs text-muted-foreground">
              Tempo médio:{' '}
              <span className="font-medium text-foreground">
                {formatResponseTime(round.avgResponseTimeMs)}
              </span>
            </span>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground mt-1">Sem dados suficientes ainda</p>
      )}
    </DashPanel>
  );
}

export function GamesModule() {
  const { filteredProfiles, profilesLoading } = useDashboard();

  const analytics = useMemo(() => computeRealGamesAnalytics(filteredProfiles), [filteredProfiles]);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);
  const activeGameId = selectedGameId ?? analytics.overview[0]?.gameId ?? null;
  const selectedGame = activeGameId ? analytics.byGame[activeGameId] : undefined;

  return (
    <div>
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Geral de todos os jogos
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <BarChartHorizontal
            title="Taxa de acerto por jogo"
            data={analytics.overview.map((g) => ({ name: g.name, accuracy: g.accuracyPct }))}
            nameKey="name"
            valueKey="accuracy"
            onExport={() =>
              exportCardCsv(
                'jogos-geral-acerto',
                ['Jogo', 'Taxa de acerto (%)'],
                analytics.overview.map((g) => [g.name, g.accuracyPct])
              )
            }
          />
          <DashPanel padding="sm">
            <DataCardHeader title="Acertos e velocidade de resposta por jogo" />
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wide bg-muted">
                  <th className="py-1.5 px-2 rounded-l-md">Jogo</th>
                  <th className="py-1.5 px-2 text-right">Acertos</th>
                  <th className="py-1.5 px-2 text-right rounded-r-md">Velocidade de resposta</th>
                </tr>
              </thead>
              <tbody>
                {analytics.overview.map((g) => (
                  <tr key={g.gameId} className="border-t border-border">
                    <td className="py-2 px-2 text-foreground">{g.name}</td>
                    <td className="py-2 px-2 text-right text-foreground tabular-nums">{g.correct}</td>
                    <td className="py-2 px-2 text-right text-foreground tabular-nums">
                      {formatResponseTime(g.avgResponseTimeMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DashPanel>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <DashPanel padding="sm">
            <MetricStat label="Jogo mais acertado" value={analytics.mostAccurateGame?.name ?? '—'} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat label="Jogo menos acertado" value={analytics.leastAccurateGame?.name ?? '—'} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Resposta mais rápida"
              value={analytics.fastestGame?.name ?? '—'}
              suffix={
                analytics.fastestGame ? formatResponseTime(analytics.fastestGame.avgResponseTimeMs) : undefined
              }
            />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Resposta mais lenta"
              value={analytics.slowestGame?.name ?? '—'}
              suffix={
                analytics.slowestGame ? formatResponseTime(analytics.slowestGame.avgResponseTimeMs) : undefined
              }
            />
          </DashPanel>
        </div>
      </section>

      {analytics.overview.length > 0 && activeGameId && (
        <div className="mb-6">
          <Tabs value={activeGameId} onValueChange={setSelectedGameId}>
            <TabsList>
              {analytics.overview.map((game) => (
                <TabsTrigger key={game.gameId} value={game.gameId} className="text-xs">
                  {game.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {!selectedGame && analytics.overview.length === 0 && (
        <p className="text-sm text-muted-foreground">Nenhum dado de jogo encontrado para esse filtro.</p>
      )}

      {selectedGame && (
      <>
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">{selectedGame.name}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <DashPanel padding="sm">
            <MetricStat label="Acertos" value={selectedGame.correct} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat label="Erros" value={selectedGame.incorrect} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat label="Taxa de acerto" value={selectedGame.accuracyPct} suffix="%" />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Tempo médio de resposta"
              value={formatResponseTime(selectedGame.avgResponseTimeMs)}
            />
          </DashPanel>
        </div>
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Desempenho por rodada</h3>
        <RoundsTable
          title={`Rodadas — ${selectedGame.name}`}
          rounds={selectedGame.rounds}
          onExport={() =>
            exportCardCsv(
              `jogos-${selectedGame.gameId}-rodadas`,
              ['Rodada', 'Comando', 'Acertos', 'Erros', 'Taxa de acerto (%)'],
              selectedGame.rounds.map((r) => [r.order, r.prompt, r.correct, r.incorrect, r.accuracyPct])
            )
          }
        />
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Dificuldade das rodadas (erros e velocidade de resposta)
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RoundHighlightCard label="Rodada mais difícil" round={selectedGame.hardestRound} />
          <RoundHighlightCard label="Rodada mais fácil" round={selectedGame.easiestRound} />
        </div>
      </section>
      </>
      )}
    </div>
  );
}
